// HouseholdMergeReview
//
// The household-side parallel to MergeReviewWizard. Renders a per-
// field star table for the household-row's own columns
// (display_name, primary_contact_id, primary_city, etc.) and
// composes the field_overrides payload the
// `/api/admin/households/:id/merge` endpoint accepts since PR #305.
//
// No "Keep both" affordance — households don't have a secondary
// slot pattern for any of these fields. Survivor or merged wins
// per-field, that's it.

import { GitMerge, Star } from "lucide-react";
import { useEffect, useMemo, useState } from "react";

import {
  reconciliationApi,
  type Household,
  type MemberMergePair,
  type MemberMergeRequest,
} from "../api";
import { Button, Card, ErrorBanner, FormField, Pill, Row } from "../ui";
import { DestructiveActionConfirm } from "./DestructiveActionConfirm";
import {
  HOUSEHOLD_MERGEABLE_FIELDS_UI,
  buildHouseholdDefaultChoices,
  composeHouseholdFieldOverrides,
  detectHouseholdConflicts,
  type HouseholdPerFieldChoice,
  type HouseholdPerFieldChoiceMap,
  type MergeMemberOption,
} from "./household-merge-review-helpers";

export interface HouseholdMergeReviewProps {
  /** The household whose id stays. */
  survivor: Household;
  /** The household to be absorbed (hard-deleted on success). */
  merged: Household;
  /**
   * Optional pre-filled reason — useful when the parent knows the
   * merge context (e.g. "Bulk household merge from the list").
   */
  initialReason?: string;
  /**
   * Called after a successful submit. Receives the field_overrides
   * + reason so the parent can issue the actual API call (the wizard
   * is presentation-only; the parent owns the network call so error
   * handling stays consistent across surfaces).
   */
  onSubmit: (payload: {
    field_overrides: ReturnType<typeof composeHouseholdFieldOverrides>;
    reason: string;
    /** Duplicate people the user ticked to fold together with the
     *  households. Empty when nothing was ticked, or when the caller
     *  didn't turn the people section on. */
    member_merges: MemberMergeRequest[];
  }) => Promise<void>;
  onCancel: () => void;
  /**
   * When provided, shows a one-click "swap" that makes the OTHER household
   * the survivor (Grant 2026-07-10 — you can decide which one survives while
   * looking at the fields, instead of choosing blind on a prior step). Omit
   * where the survivor is fixed (bulk merge, search-picker flows).
   */
  onSwapSurvivor?: (() => void) | undefined;
  /**
   * contact-id → display-name lookup so the `primary_contact_id` row
   * renders the person's NAME instead of a raw UUID (Baker, 2026-07-14).
   * Built by the parent from the household-detail contacts it already
   * fetched — see buildContactNameMap. Optional: when a given id isn't
   * in the map the cell falls back to the raw id.
   */
  contactNameById?: Record<string, string>;
  /**
   * Turns on the "same person under two spellings" section (Grant,
   * 2026-09-17). Keyed by household id — so it survives a survivor swap —
   * and holds each household's members for the pair-by-hand dropdowns.
   * Callers that leave it out get the household-only merge they had
   * before, with no people section at all.
   */
  membersByHouseholdId?: Record<string, MergeMemberOption[]>;
  /**
   * The person already answered "these two records are the same person
   * under two surnames" (the mortgage-import card, Grant 2026-09-30).
   * The people section then also offers — ticked — the two cards whose
   * surnames differ, so the merge combines the person, not only the
   * households.
   */
  samePersonConfirmed?: boolean;
}

/** Stable identity for a pair, used as the tick key and the row key. */
function pairKeyOf(p: {
  survivor_contact_id: string;
  merged_contact_id: string;
}): string {
  return `${p.survivor_contact_id}|${p.merged_contact_id}`;
}

interface MemberPairRow {
  key: string;
  survivor_contact_id: string;
  merged_contact_id: string;
  survivor_label: string;
  merged_label: string;
  why: string;
  /** Null on a pair the user added by hand — the matcher never scored it. */
  confidence: number | null;
  addedByHand: boolean;
}

function rowFromProposal(p: MemberMergePair): MemberPairRow {
  return {
    key: pairKeyOf(p),
    survivor_contact_id: p.survivor_contact_id,
    merged_contact_id: p.merged_contact_id,
    survivor_label: p.survivor_label,
    merged_label: p.merged_label,
    why: p.why,
    confidence: p.confidence,
    addedByHand: false,
  };
}

export function HouseholdMergeReview({
  survivor,
  merged,
  initialReason,
  onSubmit,
  onCancel,
  onSwapSurvivor,
  contactNameById,
  membersByHouseholdId,
  samePersonConfirmed,
}: HouseholdMergeReviewProps) {
  const [choices, setChoices] = useState<HouseholdPerFieldChoiceMap>(() =>
    buildHouseholdDefaultChoices(
      survivor as unknown as Record<string, unknown>,
      merged as unknown as Record<string, unknown>,
    ),
  );

  useEffect(() => {
    setChoices(
      buildHouseholdDefaultChoices(
        survivor as unknown as Record<string, unknown>,
        merged as unknown as Record<string, unknown>,
      ),
    );
  }, [survivor.id, merged.id, survivor, merged]);

  // ─── Same person, two spellings ───────────────────────────────────
  const peopleSectionOn = Boolean(membersByHouseholdId);
  const [proposedPairs, setProposedPairs] = useState<MemberMergePair[] | null>(
    null,
  );
  const [pairsError, setPairsError] = useState<string | null>(null);
  const [handPairs, setHandPairs] = useState<MemberPairRow[]>([]);
  const [tickedPairKeys, setTickedPairKeys] = useState<Set<string>>(
    () => new Set(),
  );
  const [handSurvivorId, setHandSurvivorId] = useState("");
  const [handMergedId, setHandMergedId] = useState("");
  const [handError, setHandError] = useState<string | null>(null);

  // Re-asked whenever the pair of households changes, which includes the
  // survivor swap — the proposal names which record keeps its id, so it
  // is wrong the moment the survivor flips.
  useEffect(() => {
    if (!peopleSectionOn) return;
    let cancelled = false;
    setProposedPairs(null);
    setPairsError(null);
    setHandPairs([]);
    setTickedPairKeys(new Set());
    setHandSurvivorId("");
    setHandMergedId("");
    setHandError(null);
    void reconciliationApi
      .getMergeMemberPairs(survivor.id, merged.id, {
        samePersonConfirmed: samePersonConfirmed === true,
      })
      .then((res) => {
        if (cancelled) return;
        setProposedPairs(res.pairs);
        setTickedPairKeys(
          new Set(res.pairs.filter((p) => p.recommended).map(pairKeyOf)),
        );
      })
      .catch((err) => {
        if (cancelled) return;
        setProposedPairs([]);
        setPairsError(
          err instanceof Error
            ? err.message
            : "Couldn't check whether these households share a person.",
        );
      });
    return () => {
      cancelled = true;
    };
  }, [peopleSectionOn, survivor.id, merged.id, samePersonConfirmed]);

  const pairRows = useMemo<MemberPairRow[]>(
    () => [...(proposedPairs ?? []).map(rowFromProposal), ...handPairs],
    [proposedPairs, handPairs],
  );

  // A person can only be folded into one other person, so a ticked pair
  // claims both of its contacts and any other pair holding either of them
  // is locked until it is unticked.
  const claimedBy = useMemo(() => {
    const owner = new Map<string, string>();
    for (const r of pairRows) {
      if (!tickedPairKeys.has(r.key)) continue;
      owner.set(r.survivor_contact_id, r.key);
      owner.set(r.merged_contact_id, r.key);
    }
    return owner;
  }, [pairRows, tickedPairKeys]);

  function isPairLocked(r: MemberPairRow): boolean {
    if (tickedPairKeys.has(r.key)) return false;
    const a = claimedBy.get(r.survivor_contact_id);
    const b = claimedBy.get(r.merged_contact_id);
    return (a !== undefined && a !== r.key) || (b !== undefined && b !== r.key);
  }

  function togglePair(key: string) {
    setTickedPairKeys((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  }

  const survivorMembers = membersByHouseholdId?.[survivor.id] ?? [];
  const mergedMembers = membersByHouseholdId?.[merged.id] ?? [];

  function addPairByHand() {
    const keepId = handSurvivorId;
    const foldId = handMergedId;
    if (!keepId || !foldId) {
      setHandError("Pick one person from each household.");
      return;
    }
    const key = `${keepId}|${foldId}`;
    if (pairRows.some((r) => r.key === key)) {
      setHandError("Those two are already listed above.");
      return;
    }
    if (claimedBy.has(keepId) || claimedBy.has(foldId)) {
      setHandError(
        "One of those people is already paired above. Untick that pair first.",
      );
      return;
    }
    const keepLabel =
      survivorMembers.find((m) => m.id === keepId)?.label ?? keepId;
    const foldLabel =
      mergedMembers.find((m) => m.id === foldId)?.label ?? foldId;
    setHandPairs((prev) => [
      ...prev,
      {
        key,
        survivor_contact_id: keepId,
        merged_contact_id: foldId,
        survivor_label: keepLabel,
        merged_label: foldLabel,
        why: "You paired these two by hand.",
        confidence: null,
        addedByHand: true,
      },
    ]);
    setTickedPairKeys((prev) => new Set(prev).add(key));
    setHandSurvivorId("");
    setHandMergedId("");
    setHandError(null);
  }

  function removePairAddedByHand(key: string) {
    setHandPairs((prev) => prev.filter((r) => r.key !== key));
    setTickedPairKeys((prev) => {
      const next = new Set(prev);
      next.delete(key);
      return next;
    });
    setHandError(null);
  }

  const chosenMemberMerges = useMemo<MemberMergeRequest[]>(
    () =>
      pairRows
        .filter((r) => tickedPairKeys.has(r.key))
        .map((r) => ({
          survivor_contact_id: r.survivor_contact_id,
          merged_contact_id: r.merged_contact_id,
        })),
    [pairRows, tickedPairKeys],
  );

  const [showAllFields, setShowAllFields] = useState(false);
  const [reason, setReason] = useState(initialReason ?? "");
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [confirmOpen, setConfirmOpen] = useState(false);

  const conflicts = useMemo(
    () =>
      new Set(
        detectHouseholdConflicts(
          survivor as unknown as Record<string, unknown>,
          merged as unknown as Record<string, unknown>,
        ),
      ),
    [survivor, merged],
  );

  const fieldsToRender = useMemo(() => {
    if (showAllFields) return HOUSEHOLD_MERGEABLE_FIELDS_UI;
    return HOUSEHOLD_MERGEABLE_FIELDS_UI.filter((f) => conflicts.has(f.key));
  }, [showAllFields, conflicts]);

  const hiddenFieldCount =
    HOUSEHOLD_MERGEABLE_FIELDS_UI.length - conflicts.size;

  function setChoice(field: string, choice: HouseholdPerFieldChoice) {
    setChoices((prev) => ({ ...prev, [field]: choice }));
  }

  // Two-phase submit so DestructiveActionConfirm can sit between
  // "user clicked Merge" and the actual API call:
  //   submit()         — validates the reason field and opens the
  //                      confirm modal
  //   executeSubmit()  — runs the merge call after user confirms
  // Replaces a pre-2026-05-24 window.confirm dialog (audit-sweep PR E).
  function submit() {
    if (reason.trim() === "") {
      setSubmitError("Reason is required.");
      return;
    }
    setSubmitError(null);
    setConfirmOpen(true);
  }

  async function executeSubmit() {
    setSubmitting(true);
    setSubmitError(null);
    try {
      const overrides = composeHouseholdFieldOverrides(choices);
      await onSubmit({
        field_overrides: overrides,
        reason: reason.trim(),
        member_merges: chosenMemberMerges,
      });
      setConfirmOpen(false);
    } catch (err) {
      if (err instanceof Error) {
        setSubmitError(err.message);
      } else {
        setSubmitError("Unknown error during merge");
      }
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <Card className="merge-review-wizard">
      <header className="merge-review-wizard-header">
        <Row gap={12} align="center">
          <GitMerge size={22} strokeWidth={1.5} />
          <h2 style={{ margin: 0 }}>Review &amp; merge households</h2>
        </Row>
        <p className="muted" style={{ margin: "6px 0 0", fontSize: 14 }}>
          The surviving household keeps its records; the other is absorbed
          into it. For each field where the two disagree, pick which value to
          keep. Fields that already match are hidden — use the toggle to see
          them all.
        </p>
      </header>

      {submitError && <ErrorBanner>{submitError}</ErrorBanner>}

      <section className="merge-review-step">
        <Row gap={12} align="baseline" justify="space-between">
          <h3 className="merge-review-step-title">
            Surviving household &amp; the one it absorbs
          </h3>
          {onSwapSurvivor && (
            <button
              type="button"
              className="merge-review-toggle"
              onClick={onSwapSurvivor}
              disabled={submitting}
              title="Make the other household the survivor instead"
            >
              ⇄ Swap — keep the other one instead
            </button>
          )}
        </Row>
        <Row gap={12} align="start" wrap>
          <HouseholdCard household={survivor} isSurvivor={true} />
          <span className="merge-review-arrow" aria-hidden="true">
            ←
          </span>
          <HouseholdCard household={merged} isSurvivor={false} />
        </Row>
      </section>

      <section className="merge-review-step">
        <Row gap={12} align="baseline" justify="space-between">
          <h3 className="merge-review-step-title">
            {conflicts.size} field{conflicts.size === 1 ? "" : "s"} the two
            disagree on
          </h3>
          {hiddenFieldCount > 0 && (
            <button
              type="button"
              className="merge-review-toggle"
              onClick={() => setShowAllFields((v) => !v)}
            >
              {showAllFields
                ? `Hide ${hiddenFieldCount} identical field${hiddenFieldCount === 1 ? "" : "s"}`
                : `Show all ${HOUSEHOLD_MERGEABLE_FIELDS_UI.length} fields`}
            </button>
          )}
        </Row>

        {fieldsToRender.length === 0 ? (
          <p className="muted" style={{ fontSize: 14 }}>
            Nothing to choose — the two households have identical values on
            every field. The merge keeps the surviving household's values and
            moves everything from the other onto it.
          </p>
        ) : (
          <div className="merge-review-table">
            <div className="merge-review-row merge-review-row--head">
              <div>Field</div>
              <div>Survivor</div>
              <div>Merged</div>
              <div></div>
            </div>
            {fieldsToRender.map((f) => {
              const survivorValue = (
                survivor as unknown as Record<string, unknown>
              )[f.key];
              const mergedValue = (
                merged as unknown as Record<string, unknown>
              )[f.key];
              const isConflict = conflicts.has(f.key);
              const choice = choices[f.key] ?? "primary";
              // The primary_contact_id column holds a raw UUID — resolve
              // it to the member's name so the row is readable (Baker,
              // 2026-07-14). Every other field renders its stored value.
              const isPrimaryContact = f.key === "primary_contact_id";
              const survivorDisplay = isPrimaryContact
                ? resolveContactName(survivorValue, contactNameById)
                : undefined;
              const mergedDisplay = isPrimaryContact
                ? resolveContactName(mergedValue, contactNameById)
                : undefined;
              return (
                <div
                  key={f.key}
                  className={`merge-review-row${isConflict ? " is-conflict" : ""}`}
                  data-field={f.key}
                >
                  <div className="merge-review-row-label">{f.label}</div>
                  <ValueCell
                    value={survivorValue}
                    display={survivorDisplay}
                    starred={choice === "primary"}
                    onStar={() => setChoice(f.key, "primary")}
                    disabled={!isConflict}
                    ariaLabel={`Keep survivor's ${f.label}`}
                  />
                  <ValueCell
                    value={mergedValue}
                    display={mergedDisplay}
                    starred={choice === "merged"}
                    onStar={() => setChoice(f.key, "merged")}
                    disabled={!isConflict}
                    ariaLabel={`Use merged household's ${f.label}`}
                  />
                  <div className="merge-review-row-action" />
                </div>
              );
            })}
          </div>
        )}
      </section>

      {peopleSectionOn && (
        <section className="merge-review-step">
          <h3 className="merge-review-step-title">
            Same person in both households?
          </h3>
          <p className="muted" style={{ margin: 0, fontSize: 13 }}>
            Merging the households moves everyone across, but it does not
            combine two cards for one person. Tick anyone below who is the
            same person spelled two ways and they become one record.
          </p>

          {pairsError && (
            <p className="merge-members-note">
              {pairsError} You can still pair people by hand below.
            </p>
          )}

          {proposedPairs === null && !pairsError ? (
            <p className="muted" style={{ margin: 0, fontSize: 13 }}>
              Checking for the same person twice…
            </p>
          ) : pairRows.length === 0 ? (
            <p className="muted" style={{ margin: 0, fontSize: 13 }}>
              Nobody looks like the same person twice. Everyone keeps their
              own record.
            </p>
          ) : (
            <div className="merge-members-list">
              {pairRows.map((r) => {
                const ticked = tickedPairKeys.has(r.key);
                const locked = isPairLocked(r);
                const inputId = `member-pair-${r.key}`;
                return (
                  <div
                    key={r.key}
                    className={`merge-members-row${ticked ? " is-on" : ""}`}
                  >
                    <input
                      id={inputId}
                      type="checkbox"
                      className="merge-members-checkbox"
                      checked={ticked}
                      disabled={submitting || locked}
                      onChange={() => togglePair(r.key)}
                    />
                    <div className="merge-members-body">
                      <label htmlFor={inputId} className="merge-members-names">
                        <strong>{r.survivor_label}</strong>
                        <span className="merge-members-arrow" aria-hidden="true">
                          ←
                        </span>
                        <span>{r.merged_label}</span>
                        {r.addedByHand ? (
                          <span className="merge-members-tag">Added by you</span>
                        ) : r.confidence !== null ? (
                          <span className="merge-members-tag">
                            {r.confidence}% match
                          </span>
                        ) : null}
                      </label>
                      <p className="merge-members-outcome">
                        Keeps {r.survivor_label} and fills in anything only{" "}
                        {r.merged_label} has. Nothing already on{" "}
                        {r.survivor_label} is overwritten.
                      </p>
                      <p className="merge-members-why">{r.why}</p>
                      {locked && (
                        <p className="merge-members-note">
                          One of these two is already being folded into
                          somebody else. Untick that pair to use this one
                          instead.
                        </p>
                      )}
                    </div>
                    {r.addedByHand && (
                      <button
                        type="button"
                        className="merge-members-remove"
                        onClick={() => removePairAddedByHand(r.key)}
                        disabled={submitting}
                      >
                        Remove
                      </button>
                    )}
                  </div>
                );
              })}
            </div>
          )}

          <div className="merge-members-manual">
            <span className="merge-members-manual-title">
              Two spellings we missed? Pair them yourself.
            </span>
            {survivorMembers.length === 0 || mergedMembers.length === 0 ? (
              <p className="merge-members-note">
                We couldn't load both households' people, so pairing by hand
                isn't available here. Merge the households first, then merge
                the two people from either person's record.
              </p>
            ) : (
              <>
                <div className="merge-members-manual-row">
                  <select
                    className="merge-members-select"
                    value={handSurvivorId}
                    disabled={submitting}
                    aria-label="Person to keep"
                    onChange={(e) => setHandSurvivorId(e.target.value)}
                  >
                    <option value="">Person to keep…</option>
                    {survivorMembers.map((m) => (
                      <option key={m.id} value={m.id}>
                        {m.label}
                      </option>
                    ))}
                  </select>
                  <span className="merge-members-arrow" aria-hidden="true">
                    ←
                  </span>
                  <select
                    className="merge-members-select"
                    value={handMergedId}
                    disabled={submitting}
                    aria-label="Person to fold in"
                    onChange={(e) => setHandMergedId(e.target.value)}
                  >
                    <option value="">Person to fold in…</option>
                    {mergedMembers.map((m) => (
                      <option key={m.id} value={m.id}>
                        {m.label}
                      </option>
                    ))}
                  </select>
                  <Button
                    size="sm"
                    variant="ghost"
                    onClick={addPairByHand}
                    disabled={submitting}
                  >
                    Add pair
                  </Button>
                </div>
                {handError && (
                  <p className="merge-members-note">{handError}</p>
                )}
              </>
            )}
          </div>
        </section>
      )}

      <section className="merge-review-step">
        <FormField
          label="Why you're merging these"
          name="merge_reason"
          required
          value={reason}
          hint="Saved to the household's history so anyone can see what happened and why (e.g. 'Same family — duplicate from the import')."
          onChange={(e) => setReason(e.target.value)}
        />
      </section>

      <footer className="merge-review-footer">
        <Row gap={8} justify="end">
          <Button variant="ghost" onClick={onCancel} disabled={submitting}>
            Cancel
          </Button>
          <Button
            variant="primary"
            onClick={submit}
            disabled={submitting}
          >
            {submitting ? "Merging…" : "Merge"}
          </Button>
        </Row>
      </footer>
      <DestructiveActionConfirm
        open={confirmOpen}
        actionName={`Merge "${merged.display_name ?? merged.id.slice(0, 8)}" into "${survivor.display_name ?? survivor.id.slice(0, 8)}"`}
        consequences={[
          `"${merged.display_name ?? merged.id.slice(0, 8)}" is absorbed into "${survivor.display_name ?? survivor.id.slice(0, 8)}" and no longer appears as a separate household.`,
          `Everything on it — service records, opportunities, transactions, care plans, activities, notes, members — moves onto "${survivor.display_name ?? survivor.id.slice(0, 8)}".`,
          `The per-field choices you made above are applied to the surviving household.`,
          ...(chosenMemberMerges.length > 0
            ? [
                `${chosenMemberMerges.length} ${chosenMemberMerges.length === 1 ? "person you ticked is" : "people you ticked are"} folded into one record each, keeping the person named on the left and filling in what only the other record had.`,
              ]
            : []),
          `Saved to the household's history with your reason.`,
        ]}
        reversibilityNote="Reversible from the household's History tab — but undoing is a separate, manual step, so review the field choices carefully first."
        confirmLabel="Merge households"
        confirmTone="danger"
        showReasonField={false}
        running={submitting}
        error={submitError}
        onCancel={() => {
          if (!submitting) setConfirmOpen(false);
        }}
        onConfirm={() => void executeSubmit()}
      />
    </Card>
  );
}

function HouseholdCard({
  household,
  isSurvivor,
}: {
  household: Household;
  isSurvivor: boolean;
}) {
  return (
    <div
      className={`merge-review-survivor-card${isSurvivor ? " is-survivor" : ""}`}
    >
      <div className="merge-review-survivor-card-header">
        {isSurvivor ? (
          <Pill variant="state-active_client">Survives</Pill>
        ) : (
          <Pill variant="state-past_client">Absorbed into survivor</Pill>
        )}
      </div>
      <h4 className="merge-review-survivor-card-name">
        {household.display_name ?? "(no display name)"}
      </h4>
      <div className="merge-review-survivor-card-meta">
        <span className="muted">
          {[household.primary_city, household.primary_state]
            .filter(Boolean)
            .join(", ") || "—"}
        </span>
      </div>
      <div className="merge-review-survivor-card-id muted">{household.id}</div>
    </div>
  );
}

function ValueCell({
  value,
  display,
  starred,
  onStar,
  disabled,
  ariaLabel,
}: {
  value: unknown;
  /** Pre-resolved display string (e.g. a contact name for a UUID field).
   *  Falls back to formatValue(value) when omitted. */
  display?: string | undefined;
  starred: boolean;
  onStar: () => void;
  disabled: boolean;
  ariaLabel: string;
}) {
  return (
    <div className="merge-review-value-cell">
      <button
        type="button"
        className={`merge-review-star${starred ? " is-on" : ""}`}
        onClick={onStar}
        disabled={disabled}
        aria-pressed={starred}
        aria-label={ariaLabel}
      >
        <Star
          size={16}
          strokeWidth={1.5}
          fill={starred ? "currentColor" : "none"}
        />
      </button>
      <span className="merge-review-value">{display ?? formatValue(value)}</span>
    </div>
  );
}

function formatValue(v: unknown): string {
  if (v === null || v === undefined) return "—";
  if (typeof v === "string") return v.length === 0 ? "—" : v;
  return String(v);
}

/**
 * Render a primary_contact_id value as the member's name. Uses the
 * parent-supplied lookup; falls back to the raw id (via formatValue)
 * when the id isn't in the map, so the cell is never blank.
 */
function resolveContactName(
  value: unknown,
  map: Record<string, string> | undefined,
): string {
  if (typeof value === "string" && value.length > 0) {
    const name = map?.[value];
    if (name) return name;
  }
  return formatValue(value);
}
